<template>
    <div class="opportunity-management">
        <el-card class="opportunity-list">
            <!-- 工具栏 -->
            <div class="toolbar">
                <div class="toolbar-row">
                    <el-button type="primary" @click="openAddDialog" size="default">
                        <el-icon><Plus /></el-icon>
                        <span class="btn-text">新增商机</span>
                    </el-button>
                    <el-input v-model="searchQuery"
                              placeholder="搜索商机/客户"
                              class="search-input"
                              @keyup.enter="fetchOpportunityList(true)"
                              clearable
                              @clear="fetchOpportunityList(true)">
                        <template #append>
                            <el-button @click="fetchOpportunityList(true)">
                                <el-icon><Search /></el-icon>
                            </el-button>
                        </template>
                    </el-input>                
                    <div class="filter-row">
                        <el-radio-group v-model="statusFilter" @change="fetchOpportunityList(true)" size="small">
                            <el-radio-button value="">全部</el-radio-button>
                            <el-radio-button value="valid">有效</el-radio-button>
                            <el-radio-button value="closed">已成交</el-radio-button>
                            <el-radio-button value="invalid">无效</el-radio-button>
                        </el-radio-group>
                        <el-select v-model="sourceFilter" clearable placeholder="来源" size="small" class="source-select" @change="fetchOpportunityList(true)">
                            <el-option v-for="s in sourceOptions" :key="s" :label="s" :value="s" />
                        </el-select>
                    </div>
                </div>
            </div>
            <!-- 桌面端表格 -->
            <el-table v-if="!isMobile"
                      ref="opportunityTable"
                      :data="rawList"
                      highlight-current-row
                      @sort-change="sortChange"
                      style="width: 100%">
                <el-table-column prop="op_createdate" label="创建" width="110" sortable="custom">
                    <template #default="{row}">
                        <el-tooltip placement="top">
                            <template #content>{{ formatDateTime(row.op_createdate) }}</template>
                            {{ formatDate(row.op_createdate) }}
                        </el-tooltip>
                    </template>
                </el-table-column>
                <el-table-column prop="opcountry" label="国家" width="120" sortable />
                <el-table-column prop="customer_companyname" label="客户名称" width="250" sortable show-overflow-tooltip />
                <el-table-column prop="ownername" label="跟进人" width="110" show-overflow-tooltip>
                    <template #default="{row}">{{ row.ownername || '-' }}</template>
                </el-table-column>
                <el-table-column prop="sellercompany" label="所属公司" width="160" show-overflow-tooltip>
                    <template #default="{row}">{{ row.sellercompany || '-' }}</template>
                </el-table-column>
                <el-table-column prop="opproducts" label="意向产品" show-overflow-tooltip />
                <el-table-column prop="opamount" label="金额" width="120" sortable align="right">
                    <template #default="{row}">
                        <span v-if="row.opamount" class="amount-text">${{ Number(row.opamount).toLocaleString() }}</span>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                <el-table-column prop="op_sources" label="来源" width="100" align="center">
                    <template #default="{row}">
                        <el-tag v-if="row.op_sources" size="small" type="info">{{ row.op_sources }}</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="opclass" label="优先级/催办" width="130" align="center">
                    <template #default="{row}">
                        <div class="prio-cell">
                            <el-tag size="small" :type="getEffClassTagType(row.eff_class)">
                                {{ getEffClassText(row.eff_class) }}{{ row.pinned ? ' 📌' : (row.eff_manual ? ' *' : '') }}
                            </el-tag>
                            <el-tag size="small" :type="getOverdueTagType(row.overdue_level)" style="margin-left:4px">
                                {{ getOverdueText(row.overdue_level) }}
                            </el-tag>
                            <el-select v-model="row.oplevel" size="small" style="width:70px; margin-top:4px"
                                       @change="(v) => updateLevel(row, v)" @click.stop>
                                <el-option label="自动" :value="3" />
                                <el-option label="普通" :value="1" />
                                <el-option label="中" :value="2" />
                                <el-option label="高" :value="4" />
                                <el-option label="置顶" :value="5" />
                            </el-select>
                            <div v-if="row.suggest_longterm" class="suggest-hint">建议转长线/无效</div>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="180">
                    <template #default="{row}">
                        <el-button size="small" type="success" @click="openDetailDrawer(row)" icon="ChatDotRound" title="详情" />
                        <el-button size="small" @click="openEditDialog(row)" icon="EditPen" title="编辑" />
                        <el-button size="small" type="danger" @click="removeOpportunity(row)" icon="Delete" title="删除" />
                    </template>
                </el-table-column>
            </el-table>

            <!-- 移动端卡片列表 -->
            <div v-else class="mobile-card-list">
                <div v-for="item in rawList" :key="item.id" class="opp-card" @click="openDetailDrawer(item)">
                    <div class="opp-card-header">
                        <div class="opp-card-title">
                            <el-tag size="small" :type="getStatusTagType(item.opstatus)">{{ getStatusText(item.opstatus) }}</el-tag>
                            <el-tag v-if="item.eff_class" size="small" :type="getEffClassTagType(item.eff_class)" style="margin-left:4px">
                                {{ getEffClassText(item.eff_class) }}{{ item.pinned ? ' 📌' : (item.eff_manual ? ' *' : '') }}
                            </el-tag>
                            <el-tag v-if="item.overdue_level && item.overdue_level !== 'normal'" size="small" :type="getOverdueTagType(item.overdue_level)" style="margin-left:4px">
                                {{ getOverdueText(item.overdue_level) }}
                            </el-tag>
                        </div>
                        <span v-if="item.opamount" class="opp-amount">${{ Number(item.opamount).toLocaleString() }}</span>
                    </div>
                    <div class="opp-card-body">
                        <div class="opp-card-row">
                            <span class="opp-label">客户</span>
                            <span class="opp-value">{{ item.customer_companyname || '-' }}</span>
                        </div>
                        <div class="opp-card-row">
                            <span class="opp-label">国家</span>
                            <span class="opp-value">{{ item.opcountry || '-' }}</span>
                            <span class="opp-label" style="margin-left:12px">来源</span>
                            <span class="opp-value">{{ item.op_sources || '-' }}</span>
                        </div>
                        <div v-if="item.opproducts" class="opp-card-row">
                            <span class="opp-label">产品</span>
                            <span class="opp-value opp-products">{{ item.opproducts }}</span>
                        </div>
                    </div>
                    <div class="opp-card-footer">
                        <span class="opp-date">{{ formatDate(item.op_createdate) }}</span>
                        <div class="opp-actions">
                            <el-button size="small" @click.stop="openEditDialog(item)" circle icon="EditPen" />
                            <el-button size="small" type="danger" @click.stop="removeOpportunity(item)" circle icon="Delete" />
                        </div>
                    </div>
                </div>
                <el-empty v-if="!rawList.length" description="暂无商机" />
            </div>

            <!-- 分页 -->
            <div class="pagination-wrapper">
                <el-pagination v-if="!isMobile"
                               v-model:current-page="pagination.currentPage"
                               v-model:page-size="pagination.pageSize"
                               :total="pagination.total"
                               layout="total, sizes, prev, pager, next, jumper"
                               :page-sizes="[20, 50, 100]"
                               @size-change="() => fetchOpportunityList(true)"
                               @current-change="() => fetchOpportunityList()" />
                <div v-else class="mobile-pagination">
                    <span class="mobile-pagination-info">{{ pagination.total }}条 · 第{{ pagination.currentPage }}页</span>
                    <el-button-group>
                        <el-button size="small" :disabled="pagination.currentPage <= 1" @click="pagination.currentPage--; fetchOpportunityList()">上一页</el-button>
                        <el-button size="small" :disabled="pagination.currentPage * pagination.pageSize >= pagination.total" @click="pagination.currentPage++; fetchOpportunityList()">下一页</el-button>
                    </el-button-group>
                </div>
            </div>
        </el-card>

        <!-- 新增/编辑商机弹窗 -->
        <el-dialog :title="editMode ? '编辑商机' : '新增商机'" v-model="addDialogVisible" width="90%" top="5vh" destroy-on-close :fullscreen="isMobile">
            <el-scrollbar :height="isMobile ? '70vh' : '65vh'">
                <el-form :model="editForm" ref="editFormRef" :label-width="isMobile ? '80px' : '120px'" label-position="left" class="edit-form">
                    <!-- 商机名称（自动拼接，只读显示） -->
                    <el-row :gutter="20">
                        <el-col :xs="24" :sm="12">
                            <el-form-item label="商机名称">
                                <el-input :model-value="autoOpName" disabled placeholder="自动生成：国家-客户简称-意向产品" />
                            </el-form-item>
                        </el-col>
                        <el-col :xs="24" :sm="6">
                            <el-form-item label="优先级">
                                <el-select v-model="editForm.opclass" style="width:100%">
                                    <el-option label="高" value="high" />
                                    <el-option label="中" value="medium" />
                                    <el-option label="普通" value="normal" />
                                </el-select>
                            </el-form-item>
                        </el-col>
                        <el-col :xs="24" :sm="6">
                            <el-form-item label="手动级别">
                                <el-select v-model="editForm.oplevel" style="width:100%">
                                    <el-option label="自动（默认）" :value="3" />
                                    <el-option label="强制普通" :value="1" />
                                    <el-option label="强制中" :value="2" />
                                    <el-option label="强制高" :value="4" />
                                    <el-option label="置顶" :value="5" />
                                </el-select>
                            </el-form-item>
                        </el-col>
                    </el-row>

                    <!-- 商机金额（自动计算，只读） -->
                    <el-row :gutter="20">
                        <el-col :xs="24" :sm="8">
                            <el-form-item label="商机金额">
                                <el-input :model-value="autoOpAmount ? '$' + autoOpAmount.toLocaleString() : '$0'" disabled />
                            </el-form-item>
                        </el-col>
                        <el-col :xs="24" :sm="16">
                            <el-form-item label="商机来源">
                                <el-radio-group v-model="editForm.op_sources" class="radio-wrap">
                                    <el-radio v-for="s in allSourceOptions" :key="s" :value="s">{{ s }}</el-radio>
                                </el-radio-group>
                            </el-form-item>
                        </el-col>
                    </el-row>

                    <el-row :gutter="20">
                        <el-col :xs="24" :sm="12">
                            <el-form-item label="商机状态">
                                <el-radio-group v-model="editForm.opstatus" class="radio-wrap">
                                    <el-radio value="valid">有效</el-radio>
                                    <el-radio value="invalid">无效</el-radio>
                                    <el-radio value="closed">已成交</el-radio>
                                </el-radio-group>
                            </el-form-item>
                        </el-col>
                    </el-row>

                    <!-- 意向产品（可输入数量的多选，类似购物车） -->
                    <el-divider>意向产品</el-divider>
                    <div v-for="(pi, idx) in editForm.productItems" :key="idx" class="product-item-row">
                        <el-row :gutter="8" align="middle">
                            <el-col :xs="24" :sm="10">
                                <el-select v-model="pi.productid" filterable placeholder="选择产品" style="width:100%"
                                    @change="(val) => handleProductItemSelect(idx, val)">
                                    <el-option v-for="p in productList" :key="p.productid" :label="p.productcode + ' - ' + (p.productname || '')" :value="p.productid">
                                        <span>{{ p.productcode }}</span>
                                        <span style="float:right;color:#909399;font-size:12px">${{ (p.unitprice || 0).toFixed(2) }}</span>
                                    </el-option>
                                </el-select>
                            </el-col>
                            <el-col :xs="12" :sm="4">
                                <el-input-number v-model="pi.qty" :min="1" size="small" style="width:100%" @change="recalcAmount" />
                            </el-col>
                            <el-col :xs="12" :sm="5">
                                <span class="product-price">${{ ((pi.unitprice || 0) * (pi.qty || 1)).toFixed(2) }}</span>
                            </el-col>
                            <el-col :xs="24" :sm="5">
                                <el-button type="danger" link icon="Delete" @click="editForm.productItems.splice(idx, 1); recalcAmount()" class="product-delete-btn" />
                            </el-col>
                        </el-row>
                    </div>
                    <el-button type="primary" size="small" icon="Plus" plain @click="addProductItem" style="margin-top:8px">添加产品</el-button>

                    <!-- 客户信息 -->
                    <el-divider>客户信息</el-divider>
                    <el-row :gutter="20">
                        <el-col :xs="24" :sm="12">
                            <el-form-item label="客户名称" prop="customercompanyid">
                                <el-autocomplete
                                    v-model="editForm.customer_companyname"
                                    :fetch-suggestions="queryCustomerSuggestions"
                                    placeholder="输入客户名称搜索"
                                    style="width: 100%"
                                    @select="handleCustomerSelect"
                                    :disabled="editMode"
                                    clearable>
                                    <template #default="{ item }">
                                        <span>{{ item.companyname }}</span>
                                        <span style="float:right;color:#909399;font-size:12px">{{ item.country || '' }}</span>
                                    </template>
                                </el-autocomplete>
                            </el-form-item>
                        </el-col>
                        <el-col :xs="24" :sm="6">
                            <el-form-item label="国家">
                                <el-input :model-value="editForm.opcountry" disabled />
                            </el-form-item>
                        </el-col>
                        <el-col :xs="24" :sm="6" v-if="!editMode">
                            <el-form-item label=" " label-width="20px">
                                <el-button @click="openCreateCustomerDrawer" :disabled="!!editForm.customercompanyid">新增客户</el-button>
                            </el-form-item>
                        </el-col>
                    </el-row>

                    <!-- 所属公司（卖方公司档案） -->
                    <el-row :gutter="20">
                        <el-col :xs="24" :sm="12">
                            <el-form-item label="所属公司">
                                <el-select v-model="editForm.profileid" placeholder="选择所属公司" clearable style="width:100%">
                                    <el-option v-for="p in profileOptions" :key="p.id"
                                               :label="(p.companyname || '') + (p.default ? '（默认）' : '')"
                                               :value="p.id" />
                                </el-select>
                            </el-form-item>
                        </el-col>
                    </el-row>
                </el-form>
            </el-scrollbar>
            <template #footer>
                <el-button @click="addDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="saveOpportunity">保存</el-button>
            </template>
        </el-dialog>

        <!-- 新增客户抽屉（复用 customers.vue 的新增功能） -->
        <el-drawer v-model="createCustomerDrawerVisible" title="新增客户" size="680px" :destroy-on-close="true">
            <el-form ref="quickCustomerFormRef" :model="quickCustomerForm" :rules="quickCustomerRules" label-width="100px" size="default">
                <el-divider content-position="left">基本信息</el-divider>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="公司名称" prop="companyname">
                            <el-input v-model="quickCustomerForm.companyname" placeholder="公司全名" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="等级" prop="class">
                            <el-select v-model="quickCustomerForm.class" placeholder="客户等级">
                                <el-option label="A-重点客户" value="A" />
                                <el-option label="B-一般客户" value="B" />
                                <el-option label="C-待开发" value="C" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="国家/地区" prop="country">
                            <el-select v-model="quickCustomerForm.country" filterable allow-create placeholder="选择或输入">
                                <el-option v-for="c in countryList" :key="c" :label="c" :value="c" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="城市">
                            <el-input v-model="quickCustomerForm.city" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="行业/关键字" prop="industry">
                            <el-input v-model="quickCustomerForm.industry" placeholder="如：电力、继电保护" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="跟进人" prop="ownerid">
                            <el-select v-model="quickCustomerForm.ownerid" placeholder="分配跟进人">
                                <el-option v-for="u in userList" :key="u.userid" :label="u.username" :value="u.userid" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="详细地址">
                    <el-input v-model="quickCustomerForm.address" />
                </el-form-item>
                <el-divider content-position="left">联系方式</el-divider>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="电话">
                            <el-input v-model="quickCustomerForm.phone" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="邮箱">
                            <el-input v-model="quickCustomerForm.email" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="网站">
                            <el-input v-model="quickCustomerForm.website" placeholder="https://" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="客户简称" prop="shortname">
                            <el-input v-model="quickCustomerForm.shortname" placeholder="用于商机名称拼接" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <!-- 联系人 -->
                <el-divider content-position="left">
                    联系人
                    <el-button type="primary" link icon="Plus" @click="quickCustomerForm.contacts.push({fullname:'',phone:'',email:'',position:'',whatsapp:'',skype:''})">添加</el-button>
                </el-divider>
                <div v-for="(ct, idx) in quickCustomerForm.contacts" :key="idx" class="contact-row">
                    <el-row :gutter="8" align="middle">
                        <el-col :span="4"><el-input v-model="ct.fullname" placeholder="姓名" size="small" /></el-col>
                        <el-col :span="3"><el-input v-model="ct.phone" placeholder="电话" size="small" /></el-col>
                        <el-col :span="4"><el-input v-model="ct.email" placeholder="邮箱" size="small" /></el-col>
                        <el-col :span="3"><el-input v-model="ct.position" placeholder="职位" size="small" /></el-col>
                        <el-col :span="3"><el-input v-model="ct.whatsapp" placeholder="WhatsApp" size="small" /></el-col>
                        <el-col :span="3"><el-input v-model="ct.skype" placeholder="Skype" size="small" /></el-col>
                        <el-col :span="2"><el-button type="danger" link icon="Delete" @click="quickCustomerForm.contacts.splice(idx,1)" /></el-col>
                    </el-row>
                </div>
            </el-form>
            <template #footer>
                <el-button @click="createCustomerDrawerVisible = false">取消</el-button>
                <el-button type="primary" @click="saveQuickCustomer" :loading="savingQuickCustomer">保存客户</el-button>
            </template>
        </el-drawer>

        <!-- 商机详情抽屉 -->
        <el-drawer v-model="followDrawerVisible" :size="drawerSize" destroy-on-close>
            <template #header>
                <div class="detail-drawer-header">
                    <span>商机：{{ currentFollow ? currentFollow.opname : '' }}</span>
                    <el-tag v-if="currentFollow" size="default" :type="getStatusTagType(currentFollow.opstatus)" style="margin-left:10px">
                        {{ getStatusText(currentFollow.opstatus) }}
                    </el-tag>
                </div>
            </template>
            <div v-if="currentFollow" class="follow-wrapper">
                <!-- 客户信息/联系人信息（在 tab 上方，不可修改） -->
                <div class="customer-info-section">
                    <div class="customer-info-header">
                        <strong>客户信息</strong>
                        <el-button type="primary" size="small" icon="Edit" @click="openEditCustomerDrawer">修改</el-button>
                    </div>
                    <el-row :gutter="16">
                        <el-col :xs="24" :sm="12">
                            <div class="info-item"><span class="info-label">公司名称：</span><span>{{ currentCustomer.companyname || '-' }}</span></div>
                        </el-col>
                        <el-col :xs="24" :sm="12">
                            <div class="info-item"><span class="info-label">国家：</span><span>{{ currentCustomer.country || '-' }}</span></div>
                        </el-col>
                        <el-col :xs="24" :sm="12">
                            <div class="info-item"><span class="info-label">电话：</span><span>{{ currentCustomer.phone || '-' }}</span></div>
                        </el-col>
                        <el-col :xs="24" :sm="12">
                            <div class="info-item"><span class="info-label">邮箱：</span><span>{{ currentCustomer.email || '-' }}</span></div>
                        </el-col>
                    </el-row>
                    <!-- 联系人 -->
                    <div class="contacts-section">
                        <div class="contacts-header">
                            <strong>联系人 ({{ followContacts.length }})</strong>
                            <el-button size="small" type="primary" link @click="showContactForm = !showContactForm">
                                {{ showContactForm ? '收起' : '新增联系人' }}
                            </el-button>
                        </div>
                        <!-- 新增/编辑联系人表单 -->
                        <div v-if="showContactForm" class="add-contact-form">
                            <el-form :model="newContact" :label-width="isMobile ? '70px' : '80px'">
                                <el-row :gutter="8">
                                    <el-col :xs="12" :sm="6"><el-form-item label="姓名"><el-input v-model="newContact.fullname" placeholder="输入姓名" /></el-form-item></el-col>
                                    <el-col :xs="12" :sm="6"><el-form-item label="职位"><el-input v-model="newContact.position" placeholder="输入职位" /></el-form-item></el-col>
                                    <el-col :xs="12" :sm="6"><el-form-item label="电话"><el-input v-model="newContact.phone" placeholder="输入电话" /></el-form-item></el-col>
                                    <el-col :xs="12" :sm="6"><el-form-item label="邮箱"><el-input v-model="newContact.email" placeholder="输入邮箱" /></el-form-item></el-col>
                                </el-row>
                                <el-row :gutter="8">
                                    <el-col :xs="12" :sm="6"><el-form-item label="WhatsApp"><el-input v-model="newContact.whatsapp" placeholder="WhatsApp" /></el-form-item></el-col>
                                    <el-col :xs="12" :sm="6"><el-form-item label="Skype"><el-input v-model="newContact.skype" placeholder="Skype" /></el-form-item></el-col>
                                    <el-col :xs="12" :sm="6"><el-form-item label="其他"><el-input v-model="newContact.othercontact" placeholder="其他联系方式" /></el-form-item></el-col>
                                    <el-col :xs="12" :sm="6"><el-form-item label="备注"><el-input v-model="newContact.notes" placeholder="备注" /></el-form-item></el-col>
                                </el-row>
                                <el-form-item>
                                    <el-button type="primary" @click="addContact" :disabled="!newContact.fullname">
                                        {{ newContact.contactid ? '更新联系人' : '添加联系人' }}
                                    </el-button>
                                </el-form-item>
                            </el-form>
                        </div>
                        <!-- 桌面端联系人表格 -->
                        <el-table v-if="!isMobile" :data="followContacts" border size="small" style="width:100%">
                            <el-table-column prop="fullname" label="姓名" width="100" />
                            <el-table-column prop="position" label="职位" width="100" />
                            <el-table-column prop="phone" label="电话" width="130" />
                            <el-table-column prop="email" label="邮箱" min-width="180" show-overflow-tooltip />
                            <el-table-column label="WhatsApp/Skype" width="160">
                                <template #default="{row}">
                                    <span v-if="row.whatsapp">WA: {{ row.whatsapp }}</span>
                                    <span v-if="row.whatsapp && row.skype"> / </span>
                                    <span v-if="row.skype">SK: {{ row.skype }}</span>
                                    <span v-if="!row.whatsapp && !row.skype">-</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="操作" width="140">
                                <template #default="{row}">
                                    <el-button size="small" @click="editContact(row)">编辑</el-button>
                                    <el-button size="small" type="danger" @click="deleteContact(row)">删除</el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                        <!-- 移动端联系人卡片 -->
                        <div v-else class="contact-card-list">
                            <div v-for="c in followContacts" :key="c.contactid" class="contact-card">
                                <div class="contact-card-name">{{ c.fullname }}<span v-if="c.position" class="contact-card-position"> ({{ c.position }})</span></div>
                                <div class="contact-card-detail" v-if="c.phone">📞 {{ c.phone }}</div>
                                <div class="contact-card-detail" v-if="c.email">✉️ {{ c.email }}</div>
                                <div class="contact-card-detail" v-if="c.whatsapp">💬 WA: {{ c.whatsapp }}</div>
                                <div class="contact-card-detail" v-if="c.skype">💬 SK: {{ c.skype }}</div>
                                <div class="contact-card-actions">
                                    <el-button size="small" @click="editContact(c)">编辑</el-button>
                                    <el-button size="small" type="danger" @click="deleteContact(c)">删除</el-button>
                                </div>
                            </div>
                            <el-empty v-if="!followContacts.length" description="暂无联系人" :image-size="50" />
                        </div>
                    </div>
                </div>

                <el-tabs v-model="detailActiveTab" class="doc-tabs">
                    <!-- 跟进记录 -->
                    <el-tab-pane label="跟进记录" name="follow">
                        <el-divider content-position="left">新增跟进</el-divider>
                        <el-form :model="followForm" :label-width="isMobile ? '70px' : '80px'" class="follow-form">
                            <el-row :gutter="12">
                                <el-col :xs="24" :sm="8">
                                    <el-form-item label="时间">
                                        <el-date-picker v-model="followForm.contactdate" type="datetime" value-format="YYYY-MM-DD HH:mm:ss" style="width:100%" />
                                    </el-form-item>
                                </el-col>
                                <el-col :xs="24" :sm="8">
                                    <el-form-item label="方式">
                                        <el-select v-model="followForm.method" placeholder="请选择" style="width:100%">
                                            <el-option v-for="m in trackingMethods" :key="m.value" :label="m.label" :value="m.value" />
                                        </el-select>
                                    </el-form-item>
                                </el-col>
                                <el-col :xs="24" :sm="8">
                                    <el-form-item label="联系人">
                                        <el-select v-model="followForm.contactid" placeholder="选择联系人" style="width:100%">
                                            <el-option v-for="c in followContacts" :key="c.contactid" :label="c.fullname" :value="c.contactid" />
                                        </el-select>
                                    </el-form-item>
                                </el-col>
                            </el-row>
                            <el-form-item label="下次跟进">
                                <el-date-picker v-model="followForm.nextfollow" type="datetime" value-format="YYYY-MM-DD HH:mm:ss" placeholder="计划下次跟进" style="width:100%" />
                            </el-form-item>
                            <el-form-item label="内容">
                                <el-input type="textarea" v-model="followForm.content" :rows="3" placeholder="记录要点 / 下步计划" />
                            </el-form-item>
                            <el-form-item>
                                <el-button type="primary" @click="submitFollow" :disabled="!followForm.content">保存跟进</el-button>
                            </el-form-item>
                        </el-form>

                        <el-divider content-position="left">历史跟进</el-divider>
                        <!-- 3列：日期、内容、联系人 -->
                        <el-table v-if="!isMobile" :data="followRecords" border size="small" style="width:100%">
                            <el-table-column label="日期" width="160">
                                <template #default="{row}">{{ formatDateTime(row.ContactDate || row.contactdate) }}</template>
                            </el-table-column>
                            <el-table-column label="内容" prop="Content" show-overflow-tooltip>
                                <template #default="{row}">{{ row.Content || row.content || '' }}</template>
                            </el-table-column>
                            <el-table-column label="联系人" width="120" show-overflow-tooltip>
                                <template #default="{row}">{{ row.ContactName || row.fullname || '-' }}</template>
                            </el-table-column>
                        </el-table>
                        <div v-else class="follow-card-list">
                            <div v-for="r in followRecords" :key="r.ID || r.RecordID" class="follow-card">
                                <div class="follow-card-date">{{ formatDateTime(r.ContactDate || r.contactdate) }}</div>
                                <div class="follow-card-content">{{ r.Content || r.content }}</div>
                                <div class="follow-card-contact">{{ r.ContactName || r.fullname || '-' }}</div>
                            </div>
                            <el-empty v-if="!followRecords.length" description="暂无跟进记录" :image-size="60" />
                        </div>
                    </el-tab-pane>

                    <!-- 单证记录（报价单 + PI/CI/PL 合并视图） -->
                    <el-tab-pane label="单证记录" name="documents">
                        <div style="display:flex; justify-content:flex-end; margin-bottom:8px">
                            <el-button type="primary" size="small" icon="Plus" @click="goCreateDocument">新建报价单</el-button>
                        </div>
                        <el-table :data="quotationRecords" border size="small" style="margin-bottom:16px">
                            <el-table-column label="类型" width="90" align="center">
                                <template #default>
                                    <el-tag size="small" type="primary">报价单</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="单号" width="180">
                                <template #default="{row}">{{ docnocal(row.documentid, row.docnumber) }}</template>
                            </el-table-column>
                            <el-table-column label="日期" width="110">
                                <template #default="{row}">{{ row.issuedate || '-' }}</template>
                            </el-table-column>
                            <el-table-column label="付款方式" width="120" show-overflow-tooltip>
                                <template #default="{row}">{{ row.paymentmethod || '-' }}</template>
                            </el-table-column>
                            <el-table-column label="币种" prop="currency" width="70" />
                            <el-table-column label="总金额" width="120" align="right">
                                <template #default="{row}">{{ currencySymbol(row.currency) }}{{ Number(row.totalamount || 0).toFixed(2) }}</template>
                            </el-table-column>
                            <el-table-column label="所属公司" min-width="140" show-overflow-tooltip>
                                <template #default="{row}">{{ row.sellercompany || '-' }}</template>
                            </el-table-column>
                            <el-table-column label="状态" width="90">
                                <template #default="{row}">
                                    <el-tag size="small" :type="getDocStatusType(row.status)">{{ getDocStatusText(row.status) }}</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="操作" min-width="340">
                                <template #default="{row}">
                                    <!-- 报价单预览（打印/导出/发邮件，复用报价单预览组件） -->
                                    <el-button size="small" icon="View" @click="previewQuote(row)">报价单</el-button>
                                    <!-- 报价单有对应 PI 时显示 PI 按钮（点击查看/生成后查看） -->
                                    <template v-if="row.pi">
                                        <el-button size="small" type="warning" @click="previewPI(row)">PI</el-button>
                                    </template>
                                    <!-- 该报价下 PI 有收款时显示 CI + PL 按钮 -->
                                    <template v-if="row.pi && row.paid">
                                        <el-button size="small" type="danger" @click="previewCI(row)">CI</el-button>
                                        <el-button size="small" type="success" @click="previewPL(row)">PL</el-button>
                                    </template>
                                    <el-button size="small" type="primary" :disabled="Number(row.status) === 3" @click="generateOrder(row)">转订单</el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                        <el-empty v-if="!quotationRecords.length" description="暂无单证记录" :image-size="60" />
                    </el-tab-pane>
                </el-tabs>
            </div>
        </el-drawer>

        <!-- 报价单/PI/CI/PL 预览抽屉（复用报价单预览组件 quotationdrawer） -->
        <el-drawer v-model="quotationPreviewVisible" size="min(1250px, 96vw)" :destroy-on-close="true"
            class="doc-preview-drawer">
            <quotationdrawer ref="quotationComp" @changed="refreshQuotationRecords" />
        </el-drawer>

        <!-- 修改客户抽屉（复用 customers.vue 中的修改功能，含联系人） -->
        <el-drawer v-model="editCustomerDrawerVisible" title="修改客户信息" size="680px" :destroy-on-close="true">
            <el-form ref="editCustomerFormRef" :model="editCustomerForm" :rules="quickCustomerRules" label-width="100px" size="default">
                <el-divider content-position="left">基本信息</el-divider>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="公司名称" prop="companyname">
                            <el-input v-model="editCustomerForm.companyname" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="等级">
                            <el-select v-model="editCustomerForm.class" placeholder="客户等级">
                                <el-option label="A-重点客户" value="A" />
                                <el-option label="B-一般客户" value="B" />
                                <el-option label="C-待开发" value="C" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="国家/地区">
                            <el-select v-model="editCustomerForm.country" filterable allow-create placeholder="选择或输入">
                                <el-option v-for="c in countryList" :key="c" :label="c" :value="c" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="行业/关键字">
                            <el-input v-model="editCustomerForm.industry" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="详细地址">
                    <el-input v-model="editCustomerForm.address" />
                </el-form-item>
                <el-divider content-position="left">联系方式</el-divider>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="电话"><el-input v-model="editCustomerForm.phone" /></el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="邮箱"><el-input v-model="editCustomerForm.email" /></el-form-item>
                    </el-col>
                </el-row>
                <!-- 联系人 -->
                <el-divider content-position="left">
                    联系人
                    <el-button type="primary" link icon="Plus" @click="editCustomerForm.contacts.push({fullname:'',phone:'',email:'',position:'',whatsapp:'',skype:''})">添加</el-button>
                </el-divider>
                <div v-for="(ct, idx) in editCustomerForm.contacts" :key="idx" class="contact-row">
                    <el-row :gutter="8" align="middle">
                        <el-col :span="4"><el-input v-model="ct.fullname" placeholder="姓名" size="small" /></el-col>
                        <el-col :span="3"><el-input v-model="ct.phone" placeholder="电话" size="small" /></el-col>
                        <el-col :span="4"><el-input v-model="ct.email" placeholder="邮箱" size="small" /></el-col>
                        <el-col :span="3"><el-input v-model="ct.position" placeholder="职位" size="small" /></el-col>
                        <el-col :span="3"><el-input v-model="ct.whatsapp" placeholder="WhatsApp" size="small" /></el-col>
                        <el-col :span="3"><el-input v-model="ct.skype" placeholder="Skype" size="small" /></el-col>
                        <el-col :span="2"><el-button type="danger" link icon="Delete" @click="editCustomerForm.contacts.splice(idx,1)" /></el-col>
                    </el-row>
                </div>
            </el-form>
            <template #footer>
                <el-button @click="editCustomerDrawerVisible = false">取消</el-button>
                <el-button type="primary" @click="saveEditCustomer" :loading="savingEditCustomer">保存</el-button>
            </template>
        </el-drawer>
    </div>
</template>

<script>
    import { Plus, Search, EditPen, Delete, ChatDotRound } from '@element-plus/icons-vue'
    import axios from 'axios'
    import Qs from 'qs'
    import moment from 'moment'
    import QuotationDrawer from './quotationdrawer.vue'

    export default {
        name: 'OpportunitiesPage',
        components: { Plus, Search, EditPen, Delete, ChatDotRound, quotationdrawer: QuotationDrawer },
        data() {
            return {
                isMobile: window.innerWidth < 768,
                rawList: [],
                searchQuery: '',
                statusFilter: 'valid',
                sourceFilter: '',
                sourceOptions: [],
                loading: false,
                pagination: {
                    currentPage: 1,
                    pageSize: 50,
                    total: 0,
                    sortProp: 'op_createdate',
                    sortOrder: 'descending'
                },
                countryOptions: [
                    { value: 'CN', label: '中国' }, { value: 'US', label: '美国' },
                    { value: 'JP', label: '日本' }, { value: 'UK', label: '英国' },
                    { value: 'DE', label: '德国' }, { value: 'KR', label: '韩国' },
                    { value: 'IN', label: '印度' }, { value: 'VN', label: '越南' },
                    { value: 'BR', label: '巴西' }, { value: 'AU', label: '澳大利亚' },
                    { value: 'CA', label: '加拿大' }, { value: 'FR', label: '法国' },
                    { value: 'IT', label: '意大利' }, { value: 'ES', label: '西班牙' },
                    { value: 'RU', label: '俄罗斯' }, { value: 'TH', label: '泰国' },
                    { value: 'ID', label: '印尼' }, { value: 'PH', label: '菲律宾' },
                    { value: 'MY', label: '马来西亚' }, { value: 'SA', label: '沙特' },
                    { value: 'AE', label: '阿联酋' }, { value: 'NG', label: '尼日利亚' },
                    { value: 'ZA', label: '南非' }, { value: 'PK', label: '巴基斯坦' },
                    { value: 'BD', label: '孟加拉' }, { value: 'EG', label: '埃及' },
                    { value: 'MX', label: '墨西哥' }, { value: 'CO', label: '哥伦比亚' },
                    { value: 'CL', label: '智利' }, { value: 'PE', label: '秘鲁' }
                ],
                countryList: [], // 从 system.asp 获取的国家列表
                userList: [],    // 从 system.asp 获取的用户列表
                trackingMethods: [
                    { value: 'email', label: '邮件' }, { value: 'phone', label: '电话' },
                    { value: 'whatsapp', label: 'WhatsApp' }, { value: 'wechat', label: '微信' },
                    { value: 'meeting', label: '面谈' }, { value: 'exhibition', label: '展会' },
                    { value: 'visit', label: '客户来访' }
                ],
                productList: [], // 产品列表（用于意向产品多选）
                profileOptions: [], // 卖方公司档案列表（system.asp profile Selection=all）

                // 新增/编辑商机
                addDialogVisible: false,
                editMode: false,
                editForm: this.makeEmptyOpportunity(),

                // 快速新增客户
                createCustomerDrawerVisible: false,
                savingQuickCustomer: false,
                quickCustomerForm: {
                    companyname: '', country: '', industry: '', class: 'C',
                    ownerid: '', city: '', address: '', phone: '', email: '',
                    website: '', shortname: '', contacts: []
                },
                quickCustomerRules: {
                    companyname: [{ required: true, message: '请输入公司名称', trigger: 'blur' }],
                    country: [{ required: true, message: '请选择国家', trigger: 'change' }]
                },

                // 详情抽屉相关
                followDrawerVisible: false,
                quotationPreviewVisible: false,  // 报价单/PI/CI/PL 预览抽屉
                detailActiveTab: 'follow',
                showContactForm: false,
                currentFollow: null,
                followRecords: [],
                followContacts: [],
                quotationRecords: [],

                // 跟进表单
                followForm: {
                    contactdate: moment().format('YYYY-MM-DD HH:mm:ss'),
                    method: 'phone',
                    contactid: '',
                    nextfollow: '',
                    content: ''
                },

                // 客户信息（详情抽屉中）
                currentCustomer: {},

                // 修改客户抽屉
                editCustomerDrawerVisible: false,
                savingEditCustomer: false,
                editCustomerForm: {
                    companyid: null, companyname: '', country: '', industry: '',
                    class: 'C', city: '', address: '', phone: '', email: '',
                    website: '', shortname: '', contacts: []
                },

                // 联系人新增/编辑
                newContact: {
                    fullname: '', position: '', phone: '', email: '',
                    whatsapp: '', skype: '', othercontact: '', notes: ''
                },

                customerOptions: [],
                customerLoading: false
            }
        },
        computed: {
            drawerSize() {
                const w = window.innerWidth
                if (w < 768) return '100%'
                if (w < 1024) return '80%'
                return '65%'
            },
            // 自动拼接商机名称
            autoOpName() {
                const country = this.editForm.opcountry || ''
                const shortName = this.editForm.customerShortName || ''
                const products = this.getProductItemsText()
                return (country && shortName) ? (country + '-' + shortName + '-' + (products || '未指定产品')) : ''
            },
            // 自动计算商机金额
            autoOpAmount() {
                if (!this.editForm.productItems || !this.editForm.productItems.length) return 0
                return this.editForm.productItems.reduce((sum, pi) => {
                    return sum + (Number(pi.unitprice) || 0) * (Number(pi.qty) || 1)
                }, 0)
            },
            // 商机来源候选：动态列表 + 当前存量值兜底（防编辑旧数据丢来源）
            allSourceOptions() {
                const list = Array.isArray(this.sourceOptions) ? [...this.sourceOptions] : []
                const cur = this.editForm.op_sources
                if (cur && !list.includes(cur)) {
                    list.push(cur)
                }
                return list
            }
        },
        watch: {
            '$route.query'(q) {
                if (q.opportunityid) {
                    this.openOpportunityFromRoute(parseInt(q.opportunityid, 10))
                }
            }
        },
        created() {
            const prefill = this.$route.query
            if (prefill.companyid) {
                this.editForm = this.makeEmptyOpportunity()
                this.editForm.customercompanyid = parseInt(prefill.companyid) || null
                this.editForm.customer_companyname = prefill.companyname || ''
                this.editForm.opcountry = prefill.country || ''
                this.addDialogVisible = true
            }
            if (prefill.opportunityid) {
                this.openOpportunityFromRoute(parseInt(prefill.opportunityid, 10))
            }
            this.fetchOpportunityList()
            this.fetchSourceOptions()
            this.fetchProductList()
            this.fetchCountryList()
            this.fetchUserList()
            this.fetchProfileList()
            window.addEventListener('resize', this.onResize)
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.onResize)
        },
        methods: {
            onResize() { this.isMobile = window.innerWidth < 768 },

            // ============ 数据加载 ============
            async fetchSourceOptions() {
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/system.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: { action: 'get_setting', key: 'opportunity_sources' }
                    })
                    let payload = res.data
                    if (typeof payload === 'string') {
                        try { payload = JSON.parse(payload.trim()) } catch (e) {}
                    }
                    const raw = (payload && payload.value) ? payload.value : 'MIC;Google;FB;Ins;展会;个人开发'
                    this.sourceOptions = raw.split(';').map(s => s.trim()).filter(s => s !== '')
                } catch (e) {
                    console.error('fetchSourceOptions error', e)
                    this.sourceOptions = ['MIC', 'Google', 'FB', 'Ins', '展会', '个人开发']
                }
            },
            async fetchOpportunityList(resetPage = false) {
                if (resetPage) this.pagination.currentPage = 1
                this.loading = true
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/opportunitiesapi.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: {
                            action: 'list', page: this.pagination.currentPage,
                            size: this.pagination.pageSize, sort: this.pagination.sortProp,
                            order: this.pagination.sortOrder, status: this.statusFilter,
                            source: this.sourceFilter, query: this.searchQuery
                        }
                    })
                    let payload = res.data
                    // ASP include 可能产生前置空白，导致 axios 返回字符串
                    if (typeof payload === 'string') {
                        try { payload = JSON.parse(payload.trim()) } catch (e) {}
                    }
                    if (payload && payload.list) {
                        this.rawList = payload.list
                        this.pagination.total = payload.total || payload.list.length
                    } else {
                        this.rawList = []
                        this.pagination.total = 0
                    }
                } catch (err) {
                    console.error('fetchOpportunityList error', err)
                } finally { this.loading = false }
            },
            async fetchProductList() {
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/products.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: { action: 'products' }
                    })
                    this.productList = res.data.products || []
                } catch (e) { console.error(e) }
            },
            async fetchCountryList() {
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/system.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: { action: 'countries' }
                    })
                    this.countryList = res.data || []
                } catch (e) { console.error(e) }
            },
            async fetchUserList() {
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/system.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: { action: 'users' }
                    })
                    this.userList = res.data || []
                } catch (e) { console.error(e) }
            },
            async fetchProfileList() {
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/system.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: { action: 'profile', Selection: 'all' }
                    })
                    let data = res.data
                    // ASP include 可能产生前置空白，导致 axios 返回字符串
                    if (typeof data === 'string') {
                        try { data = JSON.parse(data.trim()) } catch (e) {}
                    }
                    if (Array.isArray(data)) {
                        this.profileOptions = data
                    } else if (data && data.list) {
                        this.profileOptions = data.list
                    } else if (data && data.profiles) {
                        this.profileOptions = data.profiles
                    } else {
                        this.profileOptions = []
                    }
                    // 若当前表单未指定所属公司，默认选默认公司（用于路由预填等场景）
                    if (!this.editForm.profileid && this.profileOptions.length) {
                        const def = this.profileOptions.find(p => p.default) || this.profileOptions[0]
                        if (def) this.editForm.profileid = def.id
                    }
                } catch (e) { console.error('fetchProfileList error', e) }
            },
            sortChange({ prop, order }) {
                this.pagination.sortProp = prop || 'op_createdate'
                this.pagination.sortOrder = order || 'descending'
                this.pagination.currentPage = 1
                this.fetchOpportunityList()
            },
            // ============ 客户搜索 ============
            queryCustomerSuggestions(query, cb) {
                if (!query || query.length < 1) { cb([]); return }
                this.customerLoading = true
                axios({
                    url: '/apiserve/crmapi/customersapi.asp',
                    method: 'post',
                    transformRequest: [function (data) { return Qs.stringify(data) }],
                    data: { action: 'listcustomers', sea: query, order: 'createdate_desc', page: 1, size: 200 }
                }).then(res => {
                    // customersapi listcustomers 返回 {data: [...], total: N}
                    const rawList = res.data?.data || res.data?.list || []
                    const list = rawList.map(c => ({
                        companyid: c.companyid, companyname: c.companyname,
                        country: c.country || '', industry: c.industry || '',
                        shortname: c.shortname || '',
                        address: c.address || '', value: c.companyname,
                        profileid: c.profileid || '', sellercompany: c.sellercompany || ''
                    }))
                    this.customerOptions = list
                    cb(list)
                }).catch(() => { cb([]) }).finally(() => { this.customerLoading = false })
            },
            handleCustomerSelect(item) {
                this.editForm.customercompanyid = item.companyid
                this.editForm.customer_companyname = item.companyname
                this.editForm.opcountry = item.country || ''
                this.editForm.customerShortName = item.shortname || item.companyname.substring(0, 15)
                // 新增时默认所属公司取客户关联的 profileid
                if (item.profileid) {
                    this.editForm.profileid = item.profileid
                }
            },

            // ============ 意向产品管理 ============
            addProductItem() {
                this.editForm.productItems.push({
                    productid: null, productcode: '', productname: '',
                    unitprice: 0, qty: 1
                })
            },
            handleProductItemSelect(idx, productid) {
                const product = this.productList.find(p => p.productid === productid)
                if (product) {
                    this.editForm.productItems[idx].productid = product.productid
                    this.editForm.productItems[idx].productcode = product.productcode || ''
                    this.editForm.productItems[idx].productname = product.productname || ''
                    this.editForm.productItems[idx].unitprice = Number(product.unitprice) || 0
                    this.editForm.productItems[idx].qty = 1
                }
                this.recalcAmount()
            },
            recalcAmount() {
                // 金额通过 computed autoOpAmount 自动计算
                this.$forceUpdate()
            },
            getProductItemsText() {
                if (!this.editForm.productItems || !this.editForm.productItems.length) return ''
                return this.editForm.productItems
                    .filter(pi => pi.productid)
                    .map(pi => pi.productcode || pi.productname || '')
                    .join(',')
            },

            // ============ 商机增删改 ============
            makeEmptyOpportunity() {
                return {
                    id: null, opname: '', customercompanyid: null, customer_companyname: '',
                    opproducts: '', opcountry: '', opamount: '', opclass: 'high',
                    oplevel: 3, opstatus: 'valid', op_sources: '',
                    customerShortName: '', profileid: '',
                    productItems: []
                }
            },
            openAddDialog() {
                this.editForm = this.makeEmptyOpportunity()
                // 新增时默认所属公司 = 默认公司
                const def = this.profileOptions.find(p => p.default) || this.profileOptions[0]
                if (def) this.editForm.profileid = def.id
                // 新增时默认来源 = 动态列表首个
                if (this.sourceOptions && this.sourceOptions.length) {
                    this.editForm.op_sources = this.sourceOptions[0]
                }
                this.editMode = false
                this.addDialogVisible = true
            },
            async openEditDialog(item) {
                const form = JSON.parse(JSON.stringify(item))
                this.editForm = {
                    ...form,
                    productItems: [],
                    customerShortName: ''
                }
                // 编辑时回填所属公司；若无则取默认公司
                if (!this.editForm.profileid && this.profileOptions.length) {
                    const def = this.profileOptions.find(p => p.default) || this.profileOptions[0]
                    if (def) this.editForm.profileid = def.id
                }
                // 从 opproducts 文本解析为 productItems 数组
                if (this.editForm.opproducts && this.productList.length) {
                    const names = this.editForm.opproducts.split(',').map(s => s.trim()).filter(Boolean)
                    names.forEach(name => {
                        const found = this.productList.find(p =>
                            p.productcode === name || p.productname === name
                        )
                        if (found) {
                            this.editForm.productItems.push({
                                productid: found.productid,
                                productcode: found.productcode || '',
                                productname: found.productname || '',
                                unitprice: Number(found.unitprice) || 0,
                                qty: 1
                            })
                        }
                    })
                }
                // 数字→字符串映射
                const statusMap = { 1: 'valid', 2: 'closed', 0: 'invalid' }
                const classMap = { 1: 'high', 2: 'medium', 3: 'normal' }
                this.editForm.opstatus = statusMap[item.opstatus] || this.editForm.opstatus
                this.editForm.opclass = classMap[item.opclass] || this.editForm.opclass
                this.editMode = true
                this.addDialogVisible = true

                // 加载客户信息获取 shortName
                let cid = this.editForm.customercompanyid
                if (cid) {
                    try {
                        const res = await axios({
                            url: '/apiserve/crmapi/customersapi.asp',
                            method: 'post',
                            transformRequest: [function (data) { return Qs.stringify(data) }],
                            data: { action: 'getcustomer', companyid: cid }
                        })
                        if (res.data && res.data.shortname) {
                            this.editForm.customerShortName = res.data.shortname
                        } else if (res.data && res.data.companyname) {
                            this.editForm.customerShortName = res.data.companyname.substring(0, 15)
                        }
                    } catch (e) { }
                }
            },
            async saveOpportunity() {
                // 商机名称由自动拼接生成
                this.editForm.opname = this.autoOpName || '未命名商机'
                this.editForm.opamount = this.autoOpAmount.toString()
                this.editForm.opproducts = this.getProductItemsText() || '未指定产品'

                let companyId = this.editForm.customercompanyid
                // 如果没有关联客户ID但有客户名，搜索
                if (!companyId && this.editForm.customer_companyname) {
                    try {
                        const searchRes = await axios({
                            url: '/apiserve/crmapi/customersapi.asp',
                            method: 'post',
                            transformRequest: [function (data) { return Qs.stringify(data) }],
                            data: { action: 'listcustomers', sea: this.editForm.customer_companyname }
                        })
                        if (searchRes.data && searchRes.data.data && searchRes.data.data.length) {
                            const found = searchRes.data.data.find(c =>
                                c.companyname === this.editForm.customer_companyname
                            )
                            if (found) { companyId = found.companyid }
                        }
                    } catch (e) { }
                }
                const payload = {
                    opportunityName: this.editForm.opname,
                    companyid: companyId || '',
                    customerName: this.editForm.customer_companyname || '',
                    products: this.editForm.opproducts,
                    country: this.editForm.opcountry || '',
                    estimatedAmount: this.editForm.opamount,
                    priority: this.editForm.opclass || 'normal',
                    level: this.editForm.oplevel || 3,
                    status: this.editForm.opstatus || 'valid',
                    op_sources: this.editForm.op_sources || '',
                    profileid: this.editForm.profileid || ''
                }
                try {
                    if (this.editMode && this.editForm.id) {
                        payload.id = this.editForm.id
                        const res = await axios({
                            url: '/apiserve/crmapi/opportunitiesapi.asp',
                            method: 'post',
                            transformRequest: [function (data) { return Qs.stringify(data) }],
                            data: { action: 'updateopportunity', ...payload }
                        })
                        if (res.data && res.data.result === 'done') {
                            this.$message.success('修改成功')
                        } else {
                            this.$message.error(res.data.error || '更新失败')
                            return
                        }
                    } else {
                        const res = await axios({
                            url: '/apiserve/crmapi/opportunitiesapi.asp',
                            method: 'post',
                            transformRequest: [function (data) { return Qs.stringify(data) }],
                            data: { action: 'addopportunity', ...payload }
                        })
                        if (res.data && res.data.result === 'done') {
                            this.$message.success('添加成功')
                            this.pagination.currentPage = 1
                            if (res.data.id) { this.editForm.id = res.data.id }
                        } else {
                            this.$message.error(res.data.error || '添加失败')
                            return
                        }
                    }
                    this.addDialogVisible = false
                    this.fetchOpportunityList()
                } catch (err) { console.error('saveOpportunity error', err); this.$message.error('保存失败') }
            },
            removeOpportunity(item) {
                this.$confirm('删除后不可恢复，确认删除该商机？', '删除确认', {
                    type: 'warning', confirmButtonText: '确定删除', cancelButtonText: '取消'
                }).then(async () => {
                    try {
                        const res = await axios({
                            url: '/apiserve/crmapi/opportunitiesapi.asp',
                            method: 'post',
                            transformRequest: [function (data) { return Qs.stringify(data) }],
                            data: { action: 'deleteopportunity', id: item.id }
                        })
                        if (res.data && res.data.result === 'done') {
                            this.$message.success('删除成功')
                            this.fetchOpportunityList()
                        }
                    } catch (err) { this.$message.error('删除失败') }
                }).catch(() => { })
            },

            // ============ 快速新增客户 ============
            openCreateCustomerDrawer() {
                this.quickCustomerForm = {
                    companyname: '', country: '', industry: '', class: 'C',
                    ownerid: parseInt(localStorage.getItem('userid')) || '',
                    city: '', address: '', phone: '', email: '',
                    website: '', shortname: '', contacts: []
                }
                this.createCustomerDrawerVisible = true
            },
            async saveQuickCustomer() {
                try { await this.$refs.quickCustomerFormRef.validate() } catch { return }
                this.savingQuickCustomer = true
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/customersapi.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: {
                            action: 'savecustomer', mode: 'add',
                            companyname: this.quickCustomerForm.companyname,
                            country: this.quickCustomerForm.country,
                            industry: this.quickCustomerForm.industry,
                            class: this.quickCustomerForm.class,
                            ownerid: this.quickCustomerForm.ownerid,
                            city: this.quickCustomerForm.city,
                            address: this.quickCustomerForm.address,
                            phone: this.quickCustomerForm.phone,
                            email: this.quickCustomerForm.email,
                            website: this.quickCustomerForm.website,
                            shortname: this.quickCustomerForm.shortname
                        }
                    })
                    if (res.data && (res.data.result === 'created' || res.data.result === 'done' || res.data.companyid)) {
                        this.$message.success('客户已创建')
                        const newCompanyId = res.data.companyid
                        // 自动关联到当前商机
                        this.editForm.customercompanyid = newCompanyId
                        this.editForm.customer_companyname = this.quickCustomerForm.companyname
                        this.editForm.opcountry = this.quickCustomerForm.country
                        this.editForm.customerShortName = this.quickCustomerForm.shortname || this.quickCustomerForm.companyname.substring(0, 15)

                        // 保存联系人
                        if (this.quickCustomerForm.contacts.filter(c => c.fullname).length > 0) {
                            const conts = JSON.stringify(this.quickCustomerForm.contacts.map(c => ({
                                contactid: '', fullname: c.fullname, phone: c.phone,
                                email: c.email, position: c.position, whatsapp: c.whatsapp,
                                skype: c.skype, othercontact: '', notes: ''
                            })))
                            await axios({
                                url: '/apiserve/crmapi/customersapi.asp',
                                method: 'post',
                                transformRequest: [function (data) { return Qs.stringify(data) }],
                                data: { action: 'savecontacts', companyid: newCompanyId, contacts: conts }
                            })
                        }

                        this.createCustomerDrawerVisible = false
                    } else {
                        this.$message.error(res.data?.error || '创建客户失败')
                    }
                } catch (e) {
                    this.$message.error('创建客户失败')
                } finally {
                    this.savingQuickCustomer = false
                }
            },

            // ============ 详情抽屉 ============
            // 从路由 query 打开商机详情（Dashboard 跳转 ?opportunityid=）
            async openOpportunityFromRoute(oppId) {
                if (!oppId || isNaN(oppId)) return
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/opportunitiesapi.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: { action: 'detail', id: oppId }
                    })
                    const payload = res.data
                    const data = payload && payload.data ? payload.data : payload
                    if (data && data.id) {
                        this.openDetailDrawer({
                            id: data.id,
                            customercompanyid: data.customercompanyid || 0,
                            customer_companyname: data.customer_companyname || data.companyname || '',
                            opname: data.opname || data.Opname || '',
                            ...data
                        })
                    }
                } catch (e) {
                    this.$message.error('加载商机详情失败')
                }
            },

            async openDetailDrawer(item) {
                this.currentFollow = JSON.parse(JSON.stringify(item))
                this.followRecords = []
                this.followContacts = []
                this.quotationRecords = []
                this.detailActiveTab = 'follow'

                let companyId = item.customercompanyid
                // 如果 customercompanyid 为空，通过公司名搜索
                if (!companyId && item.customer_companyname) {
                    try {
                        const searchRes = await axios({
                            url: '/apiserve/crmapi/customersapi.asp',
                            method: 'post',
                            transformRequest: [function (data) { return Qs.stringify(data) }],
                            data: { action: 'listcustomers', sea: item.customer_companyname }
                        })
                        if (searchRes.data && searchRes.data.data && searchRes.data.data.length) {
                            const found = searchRes.data.data.find(c =>
                                c.companyname === item.customer_companyname
                            )
                            if (found && found.companyid) {
                                companyId = found.companyid
                                this.currentFollow.customercompanyid = companyId
                            }
                        }
                    } catch (e) { }
                }

                // 加载客户信息
                if (companyId) {
                    let customerLoaded = false
                    try {
                        const custRes = await axios({
                            url: '/apiserve/crmapi/customersapi.asp',
                            method: 'post',
                            transformRequest: [function (data) { return Qs.stringify(data) }],
                            data: { action: 'getcustomer', companyid: companyId }
                        })
                        if (custRes.data && custRes.data.companyid) {
                            this.currentCustomer = {
                                companyid: custRes.data.companyid || custRes.data.id,
                                companyname: custRes.data.companyname || '',
                                country: custRes.data.country || '',
                                industry: custRes.data.industry || '',
                                address: custRes.data.address || '',
                                phone: custRes.data.phone || '',
                                email: custRes.data.email || '',
                                website: custRes.data.website || '',
                                class: custRes.data.class || 'C',
                                ownerid: custRes.data.ownerid || '',
                                contacts: []
                            }
                            customerLoaded = true
                        }
                    } catch (e) { }

                    if (!customerLoaded) {
                        this.currentCustomer = {
                            companyid: companyId,
                            companyname: item.customer_companyname || '',
                            country: item.opcountry || '',
                            industry: '', address: '', phone: '', email: '',
                            website: '', class: 'C', ownerid: '', contacts: []
                        }
                    }

                    // 加载联系人
                    try {
                        const ctRes = await axios({
                            url: '/apiserve/crmapi/customersapi.asp',
                            method: 'post',
                            transformRequest: [function (data) { return Qs.stringify(data) }],
                            data: { action: 'contactlist', customerid: companyId }
                        })
                        if (ctRes.data) {
                            const list = Array.isArray(ctRes.data) ? ctRes.data : (ctRes.data.list || [])
                            this.followContacts = list.map(c => ({
                                contactid: c.contactid || '',
                                fullname: c.fullname || '',
                                phone: c.phone || '',
                                position: c.position || '',
                                email: c.email || '',
                                whatsapp: c.whatsapp || '',
                                skype: c.skype || '',
                                othercontact: c.othercontact || '',
                                notes: c.notes || ''
                            }))
                            this.currentCustomer.contacts = this.followContacts
                        }
                    } catch (e) { console.error('加载联系人失败', e) }

                    // 加载跟进记录
                    try {
                        const res = await axios({
                            url: '/apiserve/crmapi/opportunitiesapi.asp',
                            method: 'post',
                            transformRequest: [function (data) { return Qs.stringify(data) }],
                            data: { action: 'getcommunications', oppID: this.currentFollow.id }
                        })
                        this.followRecords = res.data.data.list
                    } catch (e) { console.error('获取跟进记录失败', e) }

                    // 加载单证记录（quotationlist）
                    await this.refreshQuotationRecords()
                } else {
                    this.currentCustomer = {
                        companyid: null,
                        companyname: item.customer_companyname || '',
                        country: item.opcountry || '',
                        industry: '', address: '', phone: '', email: '',
                        website: '', class: 'C', ownerid: '', contacts: []
                    }
                }

                // 设置跟进表单的智能默认值
                let defaultMethod = 'phone'
                let defaultContactId = ''
                if (this.followRecords.length > 0) {
                    const lastRecord = this.followRecords[0]
                    defaultMethod = lastRecord.Method || lastRecord.method || 'phone'
                    defaultContactId = lastRecord.ContactID || lastRecord.contactid || ''
                }

                // 根据优先级计算下次跟进时间
                let nextFollowDate = ''
                const priorityDays = { 1: 1, 2: 3, 3: 7, high: 1, medium: 3, normal: 7 }
                const days = priorityDays[this.currentFollow.opclass] || 3
                nextFollowDate = moment().add(days, 'days').format('YYYY-MM-DD HH:mm:ss')

                this.followForm = {
                    contactdate: moment().format('YYYY-MM-DD HH:mm:ss'),
                    method: defaultMethod,
                    contactid: defaultContactId,
                    nextfollow: nextFollowDate,
                    content: ''
                }
                this.followDrawerVisible = true
            },

            // ============ 跟进 ============
            async submitFollow() {
                if (!this.currentFollow) return
                if (!this.followForm.method || !this.followForm.content) {
                    this.$message.error('请填写跟进方式和内容'); return
                }
                const companyID = this.currentFollow.customercompanyid
                const oppid = this.currentFollow.id
                if (!companyID) { this.$message.error('找不到关联公司'); return }
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/opportunitiesapi.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: {
                            action: 'addcommunication',
                            companyID: companyID,
                            opportunityID: oppid,
                            ContactID: this.followForm.contactid,
                            ContactDate: this.followForm.contactdate,
                            Method: this.followForm.method,
                            NextFollowUp: this.followForm.nextfollow || '',
                            Content: this.followForm.content, IsCompleted: 0
                        }
                    })
                    if (res.data && res.data.result === 'done') {
                        this.$message.success('跟进已保存')
                        // 刷新跟进记录
                        const commRes = await axios({
                            url: '/apiserve/crmapi/opportunitiesapi.asp',
                            method: 'post',
                            transformRequest: [function (data) { return Qs.stringify(data) }],
                            data: { action: 'getcommunications', oppid: companyID }
                        })
                        if (commRes.data && commRes.data.data && commRes.data.data.list) {
                            this.followRecords = commRes.data.data.list
                            this.followRecords.sort((a, b) => {
                                const da = new Date(a.ContactDate || a.contactdate || 0)
                                const db = new Date(b.ContactDate || b.contactdate || 0)
                                return db - da
                            })
                        }
                        // 更新下次跟进默认值
                        let nextFollowDate = ''
                        const priorityDays = { 1: 1, 2: 3, 3: 7, high: 1, medium: 3, normal: 7 }
                        const days = priorityDays[this.currentFollow.opclass] || 3
                        nextFollowDate = moment().add(days, 'days').format('YYYY-MM-DD HH:mm:ss')
                        this.followForm = {
                            contactdate: moment().format('YYYY-MM-DD HH:mm:ss'),
                            method: this.followForm.method,
                            contactid: this.followForm.contactid,
                            nextfollow: nextFollowDate,
                            content: ''
                        }
                    } else {
                        this.$message.error('跟进保存失败')
                    }
                } catch (err) { this.$message.error('提交跟进失败') }
            },

            // ============ 联系人CRUD ============
            addContact() {
                const companyId = this.currentCustomer.companyid
                if (!companyId) { this.$message.warning('请先保存客户信息'); return }
                const isEdit = !!this.newContact.contactid
                const action = isEdit ? 'updatecontact' : 'addcontact'
                const data = {
                    action, fullname: this.newContact.fullname || '',
                    position: this.newContact.position || '', phone: this.newContact.phone || '',
                    email: this.newContact.email || '', whatsapp: this.newContact.whatsapp || '',
                    skype: this.newContact.skype || '', othercontact: this.newContact.othercontact || '',
                    notes: this.newContact.notes || '', customerid: companyId
                }
                if (isEdit) { data.contactid = this.newContact.contactid }
                axios({
                    url: '/apiserve/crmapi/customersapi.asp',
                    method: 'post',
                    transformRequest: [function (d) { return Qs.stringify(d) }],
                    data
                }).then(res => {
                    this.$message.success(isEdit ? '联系人更新成功' : '联系人添加成功')
                    this.fetchContactsForDetail(companyId)
                    this.newContact = { fullname: '', position: '', phone: '', email: '', whatsapp: '', skype: '', othercontact: '', notes: '' }
                }).catch(() => { this.$message.error('保存联系人失败') })
            },
            editContact(contact) {
                this.newContact = {
                    contactid: contact.contactid, fullname: contact.fullname || '',
                    position: contact.position || '', phone: contact.phone || '',
                    email: contact.email || '', whatsapp: contact.whatsapp || '',
                    skype: contact.skype || '', othercontact: contact.othercontact || '',
                    notes: contact.notes || ''
                }
            },
            deleteContact(contact) {
                this.$confirm('确定删除这个联系人吗?', '提示', { type: 'warning' }).then(() => {
                    axios({
                        url: '/apiserve/crmapi/customersapi.asp',
                        method: 'post',
                        transformRequest: [function (d) { return Qs.stringify(d) }],
                        data: { action: 'deletecontact', contactid: contact.contactid }
                    }).then(() => {
                        this.$message.success('联系人删除成功')
                        this.fetchContactsForDetail(this.currentCustomer.companyid)
                        this.newContact = { fullname: '', position: '', phone: '', email: '', whatsapp: '', skype: '', othercontact: '', notes: '' }
                    })
                }).catch(() => { })
            },
            fetchContactsForDetail(companyId) {
                if (!companyId) return
                axios({
                    url: '/apiserve/crmapi/customersapi.asp',
                    method: 'post',
                    transformRequest: [function (d) { return Qs.stringify(d) }],
                    data: { action: 'contactlist', customerid: companyId }
                }).then(res => {
                    const list = Array.isArray(res.data) ? res.data : (res.data.list || [])
                    this.followContacts = list
                    this.currentCustomer.contacts = list
                }).catch(() => { })
            },

            // ============ 修改客户抽屉 ============
            openEditCustomerDrawer() {
                this.editCustomerForm = {
                    companyid: this.currentCustomer.companyid,
                    companyname: this.currentCustomer.companyname || '',
                    country: this.currentCustomer.country || '',
                    industry: this.currentCustomer.industry || '',
                    class: this.currentCustomer.class || 'C',
                    city: this.currentCustomer.city || '',
                    address: this.currentCustomer.address || '',
                    phone: this.currentCustomer.phone || '',
                    email: this.currentCustomer.email || '',
                    website: this.currentCustomer.website || '',
                    shortname: this.currentCustomer.shortname || '',
                    contacts: JSON.parse(JSON.stringify(this.followContacts || []))
                }
                this.editCustomerDrawerVisible = true
            },
            async saveEditCustomer() {
                this.savingEditCustomer = true
                try {
                    // 保存客户信息
                    const res = await axios({
                        url: '/apiserve/crmapi/customersapi.asp',
                        method: 'post',
                        transformRequest: [function (d) { return Qs.stringify(d) }],
                        data: {
                            action: 'savecustomer', mode: 'edit',
                            companyid: this.editCustomerForm.companyid,
                            companyname: this.editCustomerForm.companyname,
                            country: this.editCustomerForm.country,
                            industry: this.editCustomerForm.industry,
                            class: this.editCustomerForm.class,
                            city: this.editCustomerForm.city,
                            address: this.editCustomerForm.address,
                            phone: this.editCustomerForm.phone,
                            email: this.editCustomerForm.email,
                            website: this.editCustomerForm.website,
                            shortname: this.editCustomerForm.shortname
                        }
                    })
                    if (res.data && (res.data.result === 'updated' || res.data.result === 'done')) {
                        // 保存联系人
                        if (this.editCustomerForm.contacts.length > 0) {
                            const conts = JSON.stringify(this.editCustomerForm.contacts.map(c => ({
                                contactid: c.contactid || '',
                                fullname: c.fullname || '', phone: c.phone || '',
                                email: c.email || '', position: c.position || '',
                                whatsapp: c.whatsapp || '', skype: c.skype || '',
                                othercontact: c.othercontact || '', notes: c.notes || ''
                            })))
                            await axios({
                                url: '/apiserve/crmapi/customersapi.asp',
                                method: 'post',
                                transformRequest: [function (d) { return Qs.stringify(d) }],
                                data: { action: 'savecontacts', companyid: this.editCustomerForm.companyid, contacts: conts }
                            })
                        }
                        this.$message.success('客户信息已更新')
                        // 刷新详情抽屉中的客户信息
                        this.currentCustomer.companyname = this.editCustomerForm.companyname
                        this.currentCustomer.country = this.editCustomerForm.country
                        this.currentCustomer.phone = this.editCustomerForm.phone
                        this.currentCustomer.email = this.editCustomerForm.email
                        this.fetchContactsForDetail(this.editCustomerForm.companyid)
                        this.editCustomerDrawerVisible = false
                    } else {
                        this.$message.error('更新客户信息失败')
                    }
                } catch (e) {
                    this.$message.error('更新客户信息失败')
                } finally {
                    this.savingEditCustomer = false
                }
            },

            // ============ 报价记录操作（单证链：报价单 -> PI -> CI/PL） ============
            // 点击 PI：已生成则直接查看，未生成则先生成再查看（不重复生成）
            async previewPI(row) {
                if (row.pi && row.pi.documentid) {
                    const { doc, cust } = this._docCust(row.pi)
                    this._openQuotationDrawer(doc, cust)
                    return
                }
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/documents.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: { action: 'generatedoc', sourcedocid: row.documentid, doctype: 2 }
                    })
                    if (res.data && res.data.success) {
                        res.data.already_exists ? this.$message.info('PI 已存在，直接查看') : this.$message.success('PI 生成成功')
                        await this.refreshQuotationRecords()
                        this.previewGeneratedDoc(res.data, row, 2)
                    } else {
                        this.$message.error(res.data && res.data.error ? res.data.error : 'PI 生成失败')
                    }
                } catch (e) { console.error('previewPI error', e); this.$message.error('PI 生成失败') }
            },
            // 点击 CI：已生成则直接查看，未生成则从 PI 生成再查看
            async previewCI(row) {
                const pi = row.pi || {}
                if (!pi.documentid) { this.$message.error('缺少 PI 单ID'); return }
                if (row.ci && row.ci.documentid) {
                    const { doc, cust } = this._docCust(row.ci)
                    this._openQuotationDrawer(doc, cust)
                    return
                }
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/documents.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: { action: 'generatedoc', sourcedocid: pi.documentid, doctype: 3 }
                    })
                    if (res.data && res.data.success) {
                        res.data.already_exists ? this.$message.info('CI 已存在，直接查看') : this.$message.success('CI 生成成功')
                        await this.refreshQuotationRecords()
                        this.previewGeneratedDoc(res.data, row, 3)
                    } else {
                        this.$message.error(res.data && res.data.error ? res.data.error : 'CI 生成失败')
                    }
                } catch (e) { console.error('previewCI error', e); this.$message.error('CI 生成失败') }
            },
            // 点击 PL：已生成则直接查看，未生成则从 PI 生成再查看
            async previewPL(row) {
                const pi = row.pi || {}
                if (!pi.documentid) { this.$message.error('缺少 PI 单ID'); return }
                if (row.pl && row.pl.documentid) {
                    const { doc, cust } = this._docCust(row.pl)
                    this._openQuotationDrawer(doc, cust)
                    return
                }
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/documents.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: { action: 'generatedoc', sourcedocid: pi.documentid, doctype: 4 }
                    })
                    if (res.data && res.data.success) {
                        res.data.already_exists ? this.$message.info('PL 已存在，直接查看') : this.$message.success('PL 生成成功')
                        await this.refreshQuotationRecords()
                        this.previewGeneratedDoc(res.data, row, 4)
                    } else {
                        this.$message.error(res.data && res.data.error ? res.data.error : 'PL 生成失败')
                    }
                } catch (e) { console.error('previewPL error', e); this.$message.error('PL 生成失败') }
            },
            goCreateDocument() {
                this.$router.push({ path: '/documents' })
            },
            async generateOrder(quote) {
                if (Number(quote.status) === 3) {
                    this.$message.warning('该单证已作废，不能转订单')
                    return
                }
                // 有 PI 时订单挂到 PI 下：到款金额在订单管理登记，作为生成 CI/PL 的前提
                const sourceDocId = (quote.pi && quote.pi.documentid) || quote.documentid || ''
                if (!sourceDocId) {
                    this.$message.error('缺少源单ID，无法转订单')
                    return
                }
                // quotationchain 不返回明细：items 为空时按源单拉取（PI 优先，否则报价单）
                let items = (quote.items || []).slice()
                if (!items.length) {
                    try {
                        const res = await axios({
                            url: '/apiserve/crmapi/documents.asp',
                            method: 'post',
                            transformRequest: [function (data) { return Qs.stringify(data) }],
                            data: { action: 'quotationitems', documentid: sourceDocId }
                        })
                        items = (res.data && (res.data.quotitems || res.data.data)) || []
                    } catch (e) { console.error('拉取源单明细失败', e) }
                }
                const pi = quote.pi || {}
                const q = {
                    fromQuote: '1',
                    documentid: sourceDocId,
                    companyid: this.currentCustomer.companyid || this.currentFollow.customercompanyid,
                    companyname: this.currentCustomer.companyname || this.currentFollow.customer_companyname,
                    currency: pi.currency || quote.currency || 'USD',
                    totalamount: (pi.totalamount != null && pi.totalamount !== '') ? pi.totalamount : (quote.totalamount || ''),
                    incoterms: pi.incoterms || quote.incoterms || '',
                    paymentmethod: pi.paymentmethod || quote.paymentmethod || '',
                    deliveryterms: pi.deliveryterms || quote.deliveryterms || '',
                    destination: pi.destination || quote.destination || '',
                    port: pi.port || quote.port || '',
                    countryoforigin: pi.countryoforigin || quote.countryoforigin || '',
                    contactid: this._pickOrderContact(pi, quote),
                    docnumber: pi.docnumber || quote.docnumber || '',
                    products: JSON.stringify(items.map(it => ({
                        productid: it.productid || '',
                        productcode: it.productcode || '',
                        description: it.description || '',
                        quantity: it.quantity || 1,
                        unitprice: it.unitprice || 0
                    })))
                }
                this.$router.push({ path: '/orders', query: q })
            },
            // 订单联系人：优先本单 Buyerno 选中的联系人，否则取客户联系人第一个
            _pickOrderContact(pi, quote) {
                const contacts = (this.currentCustomer && this.currentCustomer.contacts) || this.followContacts || []
                const bno = (pi && pi.buyerno != null && pi.buyerno !== '') ? pi.buyerno
                    : (quote && quote.buyerno != null && quote.buyerno !== '') ? quote.buyerno : ''
                if (bno !== '' && bno != null && contacts.length) {
                    const found = contacts.find(c => Number(c.contactid) === Number(bno))
                    if (found) return found.contactid
                }
                return (contacts.length ? contacts[0].contactid : '') || ''
            },
            docnocal(id, code) {
                id = '000000' + id
                id = id.substring(id.length - 6)
                return code + '-' + id
            },
            // 由单据行构造单证预览组件的 {doc, cust}（含按公司签字所需的 profileid / 链编号 sourcedocumentid）
            _docCust(row) {
                const doc = {
                    documentid: row.documentid,
                    docnumber: row.docnumber,
                    doctype: row.doctype || 2,
                    sourcedocumentid: row.sourcedocumentid || '',
                    profileid: row.profileid || '',
                    companyid: row.companyid || (this.currentCustomer && this.currentCustomer.companyid) || '',
                    issuedate: row.issuedate || '',
                    totalamount: row.totalamount || '',
                    currency: row.currency || '',
                    incoterms: row.incoterms || '',
                    paymentmethod: row.paymentmethod || '',
                    destination: row.destination || '',
                    port: row.port || '',
                    countryoforigin: row.countryoforigin || '',
                    deliveryterms: row.deliveryterms || '',
                    validitydate: row.validitydate || ''
                }
                const cust = {
                    companyname: (this.currentCustomer && this.currentCustomer.companyname) || '',
                    contacts: (this.currentCustomer && this.currentCustomer.contacts) || [],
                    address: (this.currentCustomer && this.currentCustomer.address) || '',
                    ownerid: (this.currentCustomer && this.currentCustomer.ownerid) || ''
                }
                return { doc, cust }
            },
            async refreshQuotationRecords() {
                const companyId = (this.currentCustomer && this.currentCustomer.companyid) ||
                    (this.currentFollow && this.currentFollow.customercompanyid)
                if (!companyId) return
                try {
                    const qRes = await axios({
                        url: '/apiserve/crmapi/documents.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: { action: 'quotationchain', companyid: companyId }
                    })
                    if (qRes.data && qRes.data.documents) {
                        this.quotationRecords = qRes.data.documents
                    } else if (qRes.data && Array.isArray(qRes.data.data)) {
                        this.quotationRecords = qRes.data.data
                    } else if (Array.isArray(qRes.data)) {
                        this.quotationRecords = qRes.data
                    }
                } catch (e) { console.error('获取单证记录失败', e) }
            },
            previewGeneratedDoc(genResult, row) {
                const { doc, cust } = this._docCust(row)
                doc.documentid = genResult.documentid || doc.documentid
                doc.docnumber = genResult.docnumber || doc.docnumber
                doc.doctype = genResult.doctype || doc.doctype
                this._openQuotationDrawer(doc, cust)
            },
            // 报价单预览/打印（复用报价单预览组件）
            previewQuote(row) {
                const { doc, cust } = this._docCust(row)
                this._openQuotationDrawer(doc, cust)
            },
            // 打开单证预览抽屉：el-drawer destroy-on-close → 先开抽屉再 openDoc
            _openQuotationDrawer(doc, cust) {
                this.quotationPreviewVisible = true
                this.$nextTick(() => {
                    setTimeout(() => {
                        if (this.$refs.quotationComp) this.$refs.quotationComp.openDoc(doc, cust)
                    }, 50)
                })
            },

            // ============ 工具方法 ============
            formatDateTime(v) { return v ? moment(v).format('YYYY-MM-DD HH:mm') : '-' },
            formatDate(v) { return v ? moment(v).format('YYYY-MM-DD') : '-' },
            currencySymbol(currency) {
                const map = { USD: '$', EUR: '€', CNY: '¥', RMB: '¥', JPY: '¥', GBP: '£', AUD: 'A$', CAD: 'C$', HKD: 'HK$', SGD: 'S$', TWD: 'NT$' }
                if (!currency) return '$'
                const upper = String(currency).toUpperCase()
                return map[upper] || (upper + ' ')
            },
            getDocTypeTag(doctype) {
                const map = { 2: 'primary', 3: 'warning', 4: 'success' }
                return map[doctype] || 'info'
            },
            getDocTypeText(doctype) {
                const map = { 2: 'PI', 3: 'CI', 4: 'PL' }
                return map[doctype] || '-'
            },
            getDocStatusType(status) {
                const s = Number(status)
                return { 0: 'info', 1: '', 2: 'success', 3: 'danger' }[s] || 'info'
            },
            getDocStatusText(status) {
                const s = Number(status)
                return { 0: '草稿', 1: '已发送', 2: '已确认', 3: '已作废' }[s] || '未知'
            },
            getStatusTagType(s) {
                const map = { 1: 'success', 2: 'warning', 0: 'info', valid: 'success', closed: 'warning', invalid: 'info' }
                return map[s] || ''
            },
            getStatusText(s) {
                const map = { 1: '有效', 2: '已成交', 0: '无效', valid: '有效', closed: '已成交', invalid: '无效' }
                return map[s] || s || '-'
            },
            getPriorityTagType(p) {
                const map = { 1: 'danger', 2: 'warning', 3: 'info', high: 'danger', medium: 'warning', normal: 'info' }
                return map[p] || ''
            },
            getPriorityText(p) {
                const map = { 1: '高', 2: '中', 3: '普通', high: '高', medium: '中', normal: '普通' }
                return map[p] || p || '-'
            },
            // 有效优先级（自动升降级后的 eff_class）
            getEffClassTagType(p) {
                const map = { 1: 'danger', 2: 'warning', 3: 'info' }
                return map[p] || ''
            },
            getEffClassText(p) {
                const map = { 1: '高', 2: '中', 3: '普通' }
                return map[p] || '普通'
            },
            // 催办级别
            getOverdueTagType(lv) {
                const map = { normal: 'info', warn: 'warning', overdue: 'danger' }
                return map[lv] || 'info'
            },
            getOverdueText(lv) {
                const map = { normal: '正常', warn: '催办', overdue: '超期' }
                return map[lv] || lv || '-'
            },
            // 手动级别（updatelevel 写 oplevel）
            async updateLevel(row, level) {
                try {
                    const res = await axios({
                        url: '/apiserve/crmapi/opportunitiesapi.asp',
                        method: 'post',
                        transformRequest: [function (data) { return Qs.stringify(data) }],
                        data: { action: 'updatelevel', id: row.id, oplevel: level }
                    })
                    if (res.data && res.data.result === 'done') {
                        this.$message.success('级别已更新')
                        this.fetchOpportunityList()
                    } else {
                        this.$message.error((res.data && res.data.message) || '更新失败')
                    }
                } catch (e) { this.$message.error('更新失败') }
            },
            getMethodTagType(m) {
                return { phone: 'primary', wechat: 'success', email: 'info', visit: 'warning', whatsapp: 'success', meeting: '', exhibition: 'danger' }[m] || ''
            },
            getMethodText(m) {
                return { email: '邮件', phone: '电话', whatsapp: 'WhatsApp', wechat: '微信', meeting: '面谈', exhibition: '展会', visit: '来访' }[m] || m
            }
        }
    }
</script>

<style scoped>
    .opportunity-management { display: flex; flex-direction: column; height: 100%; padding: 0; box-sizing: border-box; }
    .opportunity-list { margin-bottom: 15px; flex-shrink: 0; }
    .toolbar { margin-bottom: 15px; }
    .toolbar-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
    .filter-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .search-input { width: 260px; }
    .source-select { width: 120px; }
    .pagination-wrapper { margin-top: 15px; display: flex; justify-content: flex-end; }
    .amount-text { color: #c53030; font-weight: 600; }
    .edit-form { padding-right: 20px; }
    .prio-cell { display: flex; flex-direction: column; align-items: center; gap: 2px; }
    .suggest-hint { font-size: 11px; color: #e6a23c; line-height: 1.3; }
    .radio-wrap { flex-wrap: wrap; }
    .contact-row { margin-bottom: 8px; padding: 8px; background-color: #f5f7fa; border-radius: 4px; }
    .product-item-row { margin-bottom: 8px; padding: 8px; background-color: #f5f7fa; border-radius: 4px; }
    .product-price { font-size: 12px; color: #909399; }

    /* 详情抽屉 */
    .detail-drawer-header { display: flex; align-items: center; }
    .customer-info-section {
        background: #f5f7fa; border-radius: 8px; padding: 16px; margin-bottom: 16px;
    }
    .customer-info-header {
        display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;
    }
    .customer-info-header strong { font-size: 15px; }
    .info-item { margin-bottom: 6px; font-size: 13px; }
    .info-label { color: #909399; font-size: 12px; margin-right: 6px; }
    /* 联系人区域 */
    .contacts-section { margin-top: 12px; padding-top: 12px; border-top: 1px solid #e4e7ed; }
    .contacts-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
    .contacts-header strong { font-size: 14px; }
    .add-contact-form { background: #f9f9f9; padding: 15px; border-radius: 4px; margin-bottom: 12px; }

    .follow-wrapper { height: 100%; position: relative; }
    .follow-form { padding-bottom: 16px; }
    .follow-card-list { display: flex; flex-direction: column; gap: 8px; }
    .follow-card { background: #fff; border-radius: 6px; padding: 10px; border: 1px solid #ebeef5; }
    .follow-card-date { font-size: 12px; color: #909399; margin-bottom: 4px; }
    .follow-card-content { font-size: 13px; color: #303133; }
    .follow-card-contact { font-size: 12px; color: #606266; margin-top: 4px; }

    /* 移动端联系人卡片 */
    .contact-card-list { display: flex; flex-direction: column; gap: 8px; }
    .contact-card { background: #fff; border: 1px solid #ebeef5; border-radius: 6px; padding: 10px; }
    .contact-card-name { font-size: 14px; font-weight: 600; color: #303133; }
    .contact-card-position { font-size: 12px; color: #909399; font-weight: normal; }
    .contact-card-detail { font-size: 13px; color: #606266; margin-top: 4px; }
    .contact-card-actions { margin-top: 8px; padding-top: 6px; border-top: 1px solid #f2f3f5; display: flex; gap: 8px; }

    /* 移动端卡片样式 */
    .mobile-card-list { display: flex; flex-direction: column; gap: 10px; }
    .opp-card { background: #fff; border: 1px solid #ebeef5; border-radius: 8px; padding: 12px; cursor: pointer; }
    .opp-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
    .opp-card-title { display: flex; align-items: center; flex: 1; min-width: 0; gap: 4px; flex-wrap: wrap; }
    .opp-amount { color: #c53030; font-weight: 700; font-size: 15px; white-space: nowrap; margin-left: 8px; }
    .opp-card-body { font-size: 13px; color: #606266; }
    .opp-card-row { display: flex; align-items: baseline; margin-bottom: 4px; flex-wrap: wrap; }
    .opp-value { color: #303133; font-size: 13px; }
    .opp-products { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 70vw; }
    .opp-card-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; padding-top: 8px; border-top: 1px solid #f2f3f5; }
    .opp-date { color: #909399; font-size: 12px; }
    .opp-actions { display: flex; gap: 4px; }
    .mobile-pagination { display: flex; justify-content: space-between; align-items: center; width: 100%; }
    .mobile-pagination-info { color: #909399; font-size: 13px; }

    :deep(.el-drawer__body) { padding: 20px; }
    :deep(.el-form-item__label) { font-weight: bold; }

    @media (max-width: 768px) {
        .toolbar-row { flex-wrap: wrap; }
        .search-input { width: 100%; order: 1; }
        .btn-text { display: none; }
        .filter-row { width: 100%; }
        .source-select { width: 100%; margin-top: 4px; }
        .customer-info-header { flex-direction: column; align-items: flex-start; gap: 8px; }
        .customer-info-header .el-button { align-self: flex-end; }
        .opp-card { padding: 10px; }
        .opp-amount { font-size: 14px; }
    }

    /* Tab 移动端滚动 */
    :deep(.doc-tabs .el-tabs__nav-wrap) { overflow-x: auto; white-space: nowrap; }
    :deep(.doc-tabs .el-tabs__nav) { display: inline-flex; flex-wrap: nowrap; }
    :deep(.doc-tabs .el-tabs__item) { flex-shrink: 0; padding: 0 12px; }
    :deep(.doc-tabs .el-tabs__content) { overflow: visible; }
</style>
